/**
 * Keeps the kid school roster live. The backoffice pushes `attendance.updated`
 * over its WebSocket, which needs a single-use ticket; when the socket is not
 * available the roster is pulled every POLL_SECONDS instead, so a second
 * instructor's clicks still show up.
 */
import kidSchoolApi from '@/api/kidSchoolApi';
const POLL_SECONDS = 10;
const RECONNECT_SECONDS = 15;
const ATTENDANCE_EVENT = 'attendance.updated';
function webSocketUrl(ticket) {
    const base = (process.env.VUE_APP_KIDSCHOOL_API_URL ?? '').replace(/^http/, 'ws');
    return `${base}/ws?ticket=${encodeURIComponent(ticket)}`;
}
async function openSocket() {
    const ticket = (await kidSchoolApi.post('/api/ws/ticket')).data.ticket;
    return new WebSocket(webSocketUrl(ticket));
}
export class KidSchoolLive {
    listeners;
    socket = null;
    pollTimer = 0;
    reconnectTimer = 0;
    stopped = false;
    constructor(listeners) {
        this.listeners = listeners;
    }
    async start() {
        this.startPolling();
        await this.connect();
    }
    stop() {
        this.stopped = true;
        window.clearInterval(this.pollTimer);
        window.clearTimeout(this.reconnectTimer);
        this.socket?.close();
        this.socket = null;
    }
    async connect() {
        if (this.stopped) {
            return;
        }
        try {
            const socket = await openSocket();
            if (this.stopped) {
                socket.close();
                return;
            }
            this.socket = socket;
        }
        catch (e) {
            console.error('Živé spojení se nepodařilo otevřít', e);
            this.scheduleReconnect();
            return;
        }
        this.socket.onopen = () => {
            this.socket?.send(JSON.stringify({
                type: 'subscribe',
                center_ids: [process.env.VUE_APP_AREAL_ID],
            }));
            this.stopPolling();
        };
        this.socket.onmessage = (message) => this.handleMessage(message);
        this.socket.onclose = () => {
            this.socket = null;
            this.startPolling();
            this.scheduleReconnect();
        };
    }
    handleMessage(message) {
        try {
            const data = JSON.parse(message.data);
            if (data.type === 'event' && data.event?.type === ATTENDANCE_EVENT) {
                this.listeners.onChange();
            }
        }
        catch (e) {
            console.error('Zprávu živého spojení se nepodařilo přečíst', e);
        }
    }
    scheduleReconnect() {
        if (this.stopped) {
            return;
        }
        window.clearTimeout(this.reconnectTimer);
        this.reconnectTimer = window.setTimeout(() => this.connect(), RECONNECT_SECONDS * 1000);
    }
    startPolling() {
        if (this.stopped || this.pollTimer) {
            return;
        }
        this.pollTimer = window.setInterval(() => this.listeners.onChange(), POLL_SECONDS * 1000);
    }
    stopPolling() {
        window.clearInterval(this.pollTimer);
        this.pollTimer = 0;
    }
}
